export const useMeta = () => {

    const route = useRoute();
    const head = useLocaleHead();
    const nuxtApp = useNuxtApp();

    const { t, locale } = useI18n();

    const baseURL = "https://www.gamania.com";
    const apiBaseURL = "/wordpress/wp-json/wp/v2";

    const i18nMapping = {
        zh: '',
        en: 'en/',
        ja: 'ja/',
        ko: 'ko/',
    };

    const setMeta = async ( pageContent, image ) => {
        let imageData;
        const breadcrumbList = [];

        if ( !image ) {
            const ogImage = pageContent?.seo?.ogimage?.[ 0 ];
            if ( ogImage )
                imageData = await $fetch( `${ apiBaseURL }/media/${ ogImage }` );
        } else {
            imageData = image;
        }

        if ( pageContent?.seo?.object?.[ 0 ]?.[ 0 ]?.breadcrumblist?.[ 0 ]?.itemlistelements ) {
            pageContent?.seo?.object?.[ 0 ]?.[ 0 ]?.breadcrumblist?.[ 0 ]?.itemlistelements?.forEach(
                ( child ) => {
                    breadcrumbList.push( {
                        name: child.name,
                        item: child.item,
                    } );
                },
            );
        }

        const links = [];

        head.value.link.forEach( ( link ) => {
            if ( link.rel === 'alternate' )
                links.push( {
                    rel: link.rel,
                    href: link.href,
                    hreflang: link.hreflang,
                } );
        } );

        links.push( {
            rel: 'canonical',
            href: `${ baseURL }${ route.path }`,
        } );

        watch( locale, () => {
            if ( !route.name.includes( "gplay-id" ) ) {
                useHead( {
                    link: links,
                } );
            }
        } );

        nuxtApp.runWithContext( () => {

            if ( !route.name.includes( "gplay-id" ) ) {
                useHead( {
                    link: links,
                } );
            }

            useSeoMeta( {
                title: pageContent?.seo?.pagetitle,
                description: pageContent?.seo?.metadescription,
                ogSiteName: t( "breadcrumb.gamania" ),
                ogType: "website",
                ogUrl: `${ baseURL }${ route.path }`,
                ogTitle: pageContent?.seo?.pagetitle,
                ogDescription: pageContent?.seo?.metadescription,
                ogImage: imageData?.url || imageData?.source_url,
                ogImageWidth: "1200",
                ogImageHeight: "630",
                ogImageType: "image/jpeg",
                twitterTitle: pageContent?.seo?.pagetitle,
                twitterDescription: pageContent?.seo?.metadescription,
                twitterImage: imageData?.url || imageData?.source_url,
                twitterCard: "summary_large_image",
            } );

            useSchemaOrg( [
                defineWebPage( {
                    name: pageContent?.seo?.pagetitle,
                    description: pageContent?.seo?.metadescription,
                    primaryImageOfPage: imageData?.url || imageData?.source_url,
                    breadcrumb: {
                        "@id": `${ baseURL }${ route.path }#breadcrumb-${ route.name }`,
                    },
                } ),
                defineBreadcrumb( {
                    itemListElement: breadcrumbList,
                } ),
            ] );

        } );
    };

    const setShortMeta = async ( shortContent, image ) => {

        const links = [];
        const languageAlts = {};

        const languageAltsItem = await $fetch( `${ apiBaseURL }/short?slug=${ shortContent.slug }` );
        languageAltsItem.forEach( item => {
            languageAlts[ item.language ] = {
                id: item.id,
                url: `/${ i18nMapping[ item.language ] }gplay/${ item.id }`,
            };
        } );

        head.value.link.forEach( ( link ) => {
            if ( link.rel === 'alternate' ) {
                if ( link.hreflang === 'x-default' ) {
                    links.push( {
                        rel: link.rel,
                        href: languageAlts[ 'zh' ] ? `${ baseURL }${ languageAlts[ 'zh' ].url }` : link.href,
                        hreflang: link.hreflang,
                    } );
                } else {
                    const lang = Object.keys( languageAlts ).find( ( lang ) => link.hreflang.includes( lang ) );
                    if ( lang ) {
                        links.push( {
                            rel: link.rel,
                            href: `${ baseURL }${ languageAlts[ lang ].url }`,
                            hreflang: link.hreflang,
                        } );
                    } else {
                        links.push( {
                            rel: link.rel,
                            href: link.href,
                            hreflang: link.hreflang,
                        } );
                    }
                }
            }
        } );

        links.push( {
            rel: 'canonical',
            href: `${ baseURL }${ languageAlts[ 'zh' ].url }`,
        } );

        const breadcrumbList = [
            {
                name: t( "breadcrumb.gamania" ),
                item: `${ baseURL }/`,
            },
            {
                name: t( "breadcrumb.gplay" ),
                item: `${ baseURL }/${ i18nMapping[ shortContent?.language ] }gplay`,
            },
            {
                name: shortContent?.videoTitle,
                item: `${ baseURL }/${ i18nMapping[ shortContent?.language ] }gplay/${ shortContent?.id }`,
            },
        ];

        nuxtApp.runWithContext( () => {

            useHead( {
                link: links,
            } );

            useSeoMeta( {
                title: shortContent?.videoTitle,
                description: shortContent?.description?.join( "" ),
                ogSiteName: t( "breadcrumb.gamania" ),
                ogType: "website",
                ogUrl: `${ baseURL }/${ i18nMapping[ shortContent?.language ] }gplay/${ shortContent?.id }`,
                ogTitle: shortContent?.videoTitle,
                ogDescription: shortContent?.description?.join( "" ),
                ogImage: image?.source_url,
                ogImageWidth: "1200",
                ogImageHeight: "630",
                ogImageType: "image/jpeg",
                twitterTitle: shortContent?.videoTitle,
                twitterDescription: shortContent?.description?.join( "" ),
                twitterImage: image?.source_url,
                twitterCard: "summary_large_image",
            } );

            useSchemaOrg( [
                defineWebPage( {
                    name: shortContent?.videoTitle,
                    description: shortContent?.description?.join( "" ),
                    primaryImageOfPage: image?.source_url,
                    breadcrumb: {
                        "@id": `${ baseURL }/${ i18nMapping[ shortContent?.language ] }gplay/${ shortContent?.id }#breadcrumb-${ route.name }`,
                    },
                } ),
                defineBreadcrumb( {
                    itemListElement: breadcrumbList,
                } ),
                defineVideo( {
                    name: shortContent?.videoTitle,
                    description: shortContent?.description?.join( "" ),
                    thumbnailUrl: image?.source_url,
                    contentUrl: shortContent?.preview?.[ 0 ],
                    embedUrl: shortContent?.url,
                    uploadData: shortContent?.date,
                    duration: shortContent?.duration,
                } ),
            ] );

        } );

    };

    const setFocusNewsSchemaOrgVideo = ( videoContents ) => {

        let objects = [];

        if ( videoContents?.length > 0 ) {

            videoContents.forEach( ( videoContent ) => {
                objects.push(
                    defineVideo( {
                        name: videoContent?.meta_box?.article?.headline,
                        description: videoContent?.meta_box?.description?.join( "" ),
                        thumbnailUrl: videoContent?.featured_media?.url,
                        contentUrl:
                            videoContent?.meta_box?.video?.length > 0
                                ? videoContent?.meta_box?.video
                                : videoContent?.meta_box?.href,
                        embedUrl: `https://www.gamania.com/${ i18nMapping[ videoContent?.language ] }`,
                        uploadData: videoContent?.date?.split( " " )[ 0 ],
                    } ),
                );
            } );

            useSchemaOrg( objects );

        }

    };

    const setPostMeta = async ( pageContent, type ) => {

        let image = null;
        if ( !pageContent?.meta_box?.article?.image?.[ 0 ] ) {
            // console.warn("No meta image found for the article.");
            image = {
                source_url: `${ baseURL }/images/default-article-image.png`,
            };
        } else {
            image = await $fetch(
                `${ apiBaseURL }/media/${ pageContent?.meta_box?.article?.image?.[ 0 ] }`,
            );
        }

        nuxtApp.runWithContext( () => {

            const languageAlts = nuxtApp.payload.data?.[ `languageAlts-${ type }-${ pageContent?.slug }` ];

            const links = [];
            head.value.link.forEach( ( link ) => {
                if ( link.rel === 'alternate' ) {
                    if ( link.hreflang === 'x-default' ) {
                        links.push( {
                            rel: link.rel,
                            href: languageAlts[ 'zh' ] ? `${ baseURL }${ languageAlts[ 'zh' ].url }` : link.href,
                            hreflang: link.hreflang,
                        } );
                    } else {
                        const lang = Object.keys( languageAlts ).find( ( lang ) => link.hreflang.includes( lang ) );
                        if ( lang ) {
                            links.push( {
                                rel: link.rel,
                                href: `${ baseURL }${ languageAlts[ lang ].url }`,
                                hreflang: link.hreflang,
                            } );
                        } else {
                            links.push( {
                                rel: link.rel,
                                href: link.href,
                                hreflang: link.hreflang,
                            } );
                        }
                    }
                }
            } );

            links.push( {
                rel: 'canonical',
                href: `${ baseURL }${ languageAlts[ 'zh' ].url }`,
            } );

            const breadcrumbList = [
                {
                    name: t( "breadcrumb.gamania" ),
                    item: `${ baseURL }/`,
                },
                {
                    name: type === "news" ? t( "breadcrumb.news" ) : t( "breadcrumb.awards" ),
                    item: `${ baseURL }/${ i18nMapping[ pageContent?.language ] }${ type }`,
                },
                {
                    name: pageContent?.meta_box?.article?.headline,
                    item: `${ baseURL }/${ i18nMapping[ pageContent?.language ] }${ type }/${ pageContent?.id }`,
                },
            ];

            const articleObject = {
                author: {
                    "@id": "https://www.gamania.com/#identity",
                },
                headline: pageContent?.meta_box?.article?.headline,
                image: image?.source_url,
                description: pageContent?.meta_box?.customDescription?.join( "" ),
                dataModified: pageContent?.modified,
                datePublished: pageContent?.date,
            };

            watch( locale, async () => {
                await nextTick();
                useHead( {
                    link: links,
                } );
            } );

            useHead( {
                link: links,
            } );

            useSeoMeta( {
                title: pageContent?.meta_box?.article?.headline,
                description: pageContent?.meta_box?.customDescription?.join( "" ),
                ogSiteName: t( "breadcrumb.gamania" ),
                ogType: "website",
                ogUrl: `${ baseURL }/${ i18nMapping[ pageContent?.language ] }${ type }/${ pageContent?.id }`,
                ogTitle: pageContent?.meta_box?.article?.headline,
                ogDescription: pageContent?.meta_box?.customDescription?.join( "" ),
                ogImage: image?.source_url,
                ogImageWidth: "1200",
                ogImageHeight: "630",
                ogImageType: "image/jpeg",
                twitterTitle: pageContent?.meta_box?.article?.headline,
                twitterDescription: pageContent?.meta_box?.customDescription.join( "" ),
                twitterImage: image?.source_url,
                twitterCard: "summary_large_image",
            } );

            useSchemaOrg( [
                defineWebPage( {
                    name: pageContent?.meta_box?.article?.headline,
                    description: pageContent?.meta_box?.customDescription?.join( "" ),
                    primaryImageOfPage: image?.source_url,
                    breadcrumb: {
                        "@id": `${ baseURL }/${ i18nMapping[ pageContent?.language ] }${ type }/${ pageContent?.id }#breadcrumb-${ route.name }`,
                    },
                } ),
                defineBreadcrumb( {
                    itemListElement: breadcrumbList,
                } ),
                defineArticle( articleObject ),
            ] );

        } );

    };

    return {
        setMeta,
        setShortMeta,
        setPostMeta,
        setFocusNewsSchemaOrgVideo,
    };
};
